import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Save,
  Eye,
  FileText,
  Calendar,
  Home,
  User,
  Send,
  AlertCircle,
  Pen,
  EyeOff
} from 'lucide-react';
import { useCurrentUser } from '@/hooks/useProfiles';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { SupabasePreApprovalService } from '@/services/supabasePreApprovalService';
import { PreApprovalData } from '@/types/pre-approval';
import { VisualTemplateGallery } from './VisualTemplateGallery';
import { VisualTemplatePreview } from './VisualTemplatePreview';
import { PreApprovalSendModal } from './PreApprovalSendModal';
import { SignatureDialog, type SignatureData } from '@/components/common/SignatureDialog';
import { formatPercentage } from '@/utils/formatCurrency';
import { cn } from '@/lib/utils';
import { PRE_APPROVAL_CONSTANTS, getExpirationDate } from '@/constants/pre-approval';
import { UserService } from '@/services';
import { getPreApprovalExpirationInstant } from '@/utils/pre-approval/expiration';

import logger from '@/utils/logger';
interface TemplateLayout {
  id: string;
  name: string;
  pageSize: 'letter' | 'a4';
  orientation: 'portrait' | 'landscape';
  margins: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  elements: Array<{
    id: string;
    type: 'text' | 'image' | 'divider' | 'signature';
    content?: string;
    imageUrl?: string;
    x: number;
    y: number;
    width: number;
    height: number;
    styles: {
      fontSize: number;
      fontFamily: string;
      fontWeight: 'normal' | 'bold';
      fontStyle: 'normal' | 'italic';
      textDecoration: 'none' | 'underline';
      textAlign: 'left' | 'center' | 'right';
      color: string;
      backgroundColor?: string;
      padding: number;
      borderRadius: number;
      border?: string;
      lineHeight?: number;
    };
  }>;
  createdAt: string;
  updatedAt: string;
}

interface VisualPreApprovalWizardProps {
  onComplete?: (preApprovalId: string) => void;
  onBack?: () => void;
  prefilledData?: PreApprovalData | null;
}

// US States for dropdown
const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

// Phone number formatting helper
const formatPhoneNumber = (value: string): string => {
  // Remove all non-digit characters
  const cleaned = value.replace(/\D/g, '');

  // Format based on length
  if (cleaned.length <= 3) {
    return cleaned;
  }
  if (cleaned.length <= 6) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  }
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

// Email validation helper
const validateEmail = (email: string): string | null => {
  if (!email) {
    return 'Email is required';
  }
  // Require at least 2 characters after the dot (.com, .net, .io, etc.)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address';
  }
  return null;
};

// Currency formatting helper
const formatCurrency = (value: string): string => {
  if (!value) {
return '';
}
  const numericValue = value.replace(/[^0-9]/g, '');
  return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const VisualPreApprovalWizard: React.FC<VisualPreApprovalWizardProps> = ({
  onComplete,
  onBack,
  prefilledData
}) => {
  const navigate = useNavigate();
  const { profile, loading: profileLoading } = useCurrentUser();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateLayout | null>(null);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [showSendModal, setShowSendModal] = useState(false);
  const [savedPreApproval, setSavedPreApproval] = useState<PreApprovalData | null>(null);
  const [templateVars, setTemplateVars] = useState<Awaited<ReturnType<typeof UserService.getTemplateVariables>> | null>(null);
  const [showCoBorrower, setShowCoBorrower] = useState(
    Boolean(prefilledData?.coBorrowerInfo?.firstName || prefilledData?.coBorrowerInfo?.lastName)
  );

  // E-Signature state
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [hasLoanOfficerSignature, setHasLoanOfficerSignature] = useState(false);
  const [signatureData, setSignatureData] = useState<SignatureData | null>(null);

  const [formData, setFormData] = useState<Partial<PreApprovalData>>(() => {
    const defaultData = {
      borrowerInfo: {
        salutation: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: ''
      },
      loanDetails: {
        loanAmount: 0,
        purchasePrice: 0,
        loanType: PRE_APPROVAL_CONSTANTS.LOAN_TYPES.DEFAULT
        // downPaymentPercent, downPaymentAmount, and ltv are now calculated via useMemo
      },
      propertyInfo: {
        propertyType: PRE_APPROVAL_CONSTANTS.PROPERTY.DEFAULT_TYPE,
        occupancy: PRE_APPROVAL_CONSTANTS.PROPERTY.DEFAULT_OCCUPANCY,
        address: PRE_APPROVAL_CONSTANTS.PROPERTY.TBD_ADDRESS,
        city: '',
        state: '',
        zipCode: ''
      },
      coBorrowerInfo: {
        salutation: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: ''
      },
      expirationDate: getExpirationDate() // Default: 30 days from now
    };

    // Merge with prefilled data if available
    if (prefilledData) {
      const mergedData = {
        ...defaultData,
        ...prefilledData,
        expirationDate: prefilledData.expirationDate || prefilledData.expiresAt?.slice(0, 10) || defaultData.expirationDate,
        borrowerInfo: {
          ...defaultData.borrowerInfo,
          ...prefilledData.borrowerInfo
        },
        coBorrowerInfo: {
          ...defaultData.coBorrowerInfo,
          ...prefilledData.coBorrowerInfo
        }
      };
      return mergedData;
    }

    return defaultData;
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Check for existing signature when editing pre-approval
  useEffect(() => {
    if (prefilledData?.hasLoanOfficerSignature || prefilledData?.loanOfficerSignatureDataUrl) {
      setHasLoanOfficerSignature(true);
    }
  }, [prefilledData]);

  // Load template variables for preview footer fields
  useEffect(() => {
    if (!profile?.id || templateVars) {
      return;
    }

    let isActive = true;
    const loadTemplateVars = async () => {
      try {
        const vars = await UserService.getTemplateVariables();
        if (isActive) {
          setTemplateVars(vars || null);
        }
      } catch (error) {
        logger.warn('Unable to load template variables for pre-approval preview', error);
      }
    };

    loadTemplateVars();

    return () => {
      isActive = false;
    };
  }, [profile?.id, templateVars]);

  // Filter states based on loan officer's licensed states
  const filteredStates = useMemo(() => {
    if (!profile?.licensedStates || profile.licensedStates.length === 0) {
      return [];
    }
    return US_STATES.filter(state => profile.licensedStates?.includes(state.value));
  }, [profile?.licensedStates]);

  // Auto-calculate LTV and down payment
  // Calculate derived values using useMemo to avoid unnecessary re-renders
  // These are computed from loanAmount and purchasePrice, not stored in state
  const calculations = useMemo(() => {
    const loanAmount = formData.loanDetails?.loanAmount || 0;
    const purchasePrice = formData.loanDetails?.purchasePrice || 0;

    if (purchasePrice <= 0) {
      return {
        ltv: 0,
        downPaymentAmount: 0,
        downPaymentPercent: 0,
        requiresPMI: false
      };
    }

    const ltv = (loanAmount / purchasePrice) * 100;
    const downPaymentAmount = purchasePrice - loanAmount;
    const downPaymentPercent = (downPaymentAmount / purchasePrice) * 100;

    return {
      ltv: Math.round(ltv * PRE_APPROVAL_CONSTANTS.VALIDATION.PERCENT_PRECISION * 10) / 10,
      downPaymentAmount,
      downPaymentPercent: Math.round(downPaymentPercent * 10) / 10,
      requiresPMI: ltv > PRE_APPROVAL_CONSTANTS.VALIDATION.PMI_THRESHOLD_LTV
    };
  }, [formData.loanDetails?.loanAmount, formData.loanDetails?.purchasePrice]);

  const handleTemplateSelect = (template: TemplateLayout) => {
    setSelectedTemplate(template);
    setHasAutoSelected(true);
    setCurrentStep(2);
  };

  const handleFieldChange = (section: string, field: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section as keyof typeof prev] as Record<string, unknown>,
        [field]: value
      }
    }));

    // Clear error for this field
    setErrors(prev => {
      const key = `${section}.${field}`;
      const newErrors = { ...prev };
      delete newErrors[key];
      return newErrors;
    });
  };

  const clearCoBorrower = () => {
    setShowCoBorrower(false);
    setFormData(prev => ({
      ...prev,
      coBorrowerInfo: undefined
    }));
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors['coBorrowerInfo.firstName'];
      delete newErrors['coBorrowerInfo.lastName'];
      delete newErrors['coBorrowerInfo.email'];
      delete newErrors['coBorrowerInfo.phone'];
      return newErrors;
    });
  };

  const validateStep2 = () => {
    const newErrors: Record<string, string> = {};

    // Validate borrower info
    if (!formData.borrowerInfo?.firstName) {
      newErrors['borrowerInfo.firstName'] = 'First name is required';
    }
    if (!formData.borrowerInfo?.lastName) {
      newErrors['borrowerInfo.lastName'] = 'Last name is required';
    }

    if (showCoBorrower) {
      if (!formData.coBorrowerInfo?.firstName) {
        newErrors['coBorrowerInfo.firstName'] = 'First name is required';
      }
      if (!formData.coBorrowerInfo?.lastName) {
        newErrors['coBorrowerInfo.lastName'] = 'Last name is required';
      }
      // Co-borrower email: validate format only if provided
      const coEmail = formData.coBorrowerInfo?.email?.trim();
      if (coEmail) {
        const coEmailError = validateEmail(coEmail);
        if (coEmailError) {
          newErrors['coBorrowerInfo.email'] = coEmailError;
        }
      }
      // Co-borrower phone: validate 10 digits only if provided
      const coPhone = formData.coBorrowerInfo?.phone?.trim();
      if (coPhone) {
        const coPhoneDigits = coPhone.replace(/\D/g, '');
        if (coPhoneDigits.length !== 10) {
          newErrors['coBorrowerInfo.phone'] = 'Please enter a valid 10-digit phone number';
        }
      }
    }

    // Email validation
    const emailError = validateEmail(formData.borrowerInfo?.email || '');
    if (emailError) {
      newErrors['borrowerInfo.email'] = emailError;
    }

    // Phone validation
    if (!formData.borrowerInfo?.phone) {
      newErrors['borrowerInfo.phone'] = 'Phone is required';
    } else {
      const phoneDigits = formData.borrowerInfo.phone.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        newErrors['borrowerInfo.phone'] = 'Please enter a valid 10-digit phone number';
      }
    }

    // Validate loan details
    if (!formData.loanDetails?.loanAmount || formData.loanDetails.loanAmount <= 0) {
      newErrors['loanDetails.loanAmount'] = 'Loan amount must be greater than $0';
    }
    if (!formData.loanDetails?.purchasePrice || formData.loanDetails.purchasePrice <= 0) {
      newErrors['loanDetails.purchasePrice'] = 'Purchase price must be greater than $0';
    }
    if (formData.loanDetails?.loanAmount && formData.loanDetails?.purchasePrice) {
      if (formData.loanDetails.purchasePrice < formData.loanDetails.loanAmount) {
        newErrors['loanDetails.purchasePrice'] = 'Purchase price must be at least the loan amount';
      }
    }

    // Property validation only if not TBD
    if (formData.propertyInfo?.address !== 'TBD') {
      if (!formData.propertyInfo?.address) {
        newErrors['propertyInfo.address'] = 'Property address is required';
      }
      if (!formData.propertyInfo?.city) {
        newErrors['propertyInfo.city'] = 'City is required';
      }
      if (!formData.propertyInfo?.state) {
        newErrors['propertyInfo.state'] = 'State is required';
      }

      // ZIP code validation
      if (!formData.propertyInfo?.zipCode) {
        newErrors['propertyInfo.zipCode'] = 'ZIP code is required';
      } else {
        const zipRegex = /^\d{5}$/;
        if (!zipRegex.test(formData.propertyInfo.zipCode)) {
          newErrors['propertyInfo.zipCode'] = 'Please enter a valid 5-digit ZIP code';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 2) {
      if (!validateStep2()) {
        toast.error('Please fix the errors before continuing');
        return;
      }
    }
    setCurrentStep(prev => Math.min(prev + 1, 2));
  };

  const handleBack = () => {
    if (currentStep === 2) {
      // From Step 2, go directly to pre-approval dashboard
      navigate('/internal?section=pre-approval');
    } else if (currentStep === 1 && onBack) {
      onBack();
    } else {
      setCurrentStep(prev => Math.max(prev - 1, 1));
    }
  };

  const handleSave = async (): Promise<PreApprovalData | null> => {
    if (!selectedTemplate) {
      return null;
    }

    // Safety check: Ensure profile is loaded
    if (!profile?.id) {
      toast.error('Unable to save: User profile not loaded');
      return null;
    }

    setIsSaving(true);
    try {
      // Log template structure before saving
      logger.info('Saving pre-approval with template:', {
        templateId: selectedTemplate.id,
        templateName: selectedTemplate.name,
        hasElements: !!selectedTemplate.elements,
        elementCount: selectedTemplate.elements?.length || 0,
        signatureElements: selectedTemplate.elements?.filter(e =>
          e.type === 'signature' || e.content?.includes('{{loanOfficerSignature}}')
        ).length || 0
      });

      const existingPreApproval = savedPreApproval || (prefilledData?.id ? prefilledData as PreApprovalData : null);
      const now = new Date().toISOString();

      const preApprovalData: PreApprovalData = {
        ...(existingPreApproval || {}),
        id: existingPreApproval?.id || `pa-${Date.now()}`,
        templateId: selectedTemplate.id,
        loanOfficerId: profile.id,
        borrowerInfo: formData.borrowerInfo!,
        coBorrowerInfo: showCoBorrower && formData.coBorrowerInfo?.firstName ? {
          salutation: formData.coBorrowerInfo.salutation || '',
          firstName: formData.coBorrowerInfo.firstName,
          lastName: formData.coBorrowerInfo.lastName || '',
          email: formData.coBorrowerInfo.email || '',
          phone: formData.coBorrowerInfo.phone || ''
        } : undefined,
        loanDetails: {
          ...formData.loanDetails!,
          // Include calculated values when saving
          downPaymentAmount: calculations.downPaymentAmount,
          downPaymentPercent: calculations.downPaymentPercent
        },
        propertyInfo: formData.propertyInfo!,
        customFields: existingPreApproval?.customFields || {},
        status: existingPreApproval?.status || 'draft',
        createdAt: existingPreApproval?.createdAt || now,
        updatedAt: now,
        expiresAt: getPreApprovalExpirationInstant(formData.expirationDate),
        expirationDate: formData.expirationDate,
        calculations: {
          ltv: calculations.ltv
        },
        template_data: selectedTemplate,
        loanOfficerSignatureDate: existingPreApproval?.loanOfficerSignatureDate,
        loanOfficerSignatureDataUrl: signatureData?.dataUrl || existingPreApproval?.loanOfficerSignatureDataUrl,
        loanOfficerSignatureMethod: signatureData?.metadata.method || existingPreApproval?.loanOfficerSignatureMethod,
        loanOfficerSignatureFont: signatureData?.metadata.font || existingPreApproval?.loanOfficerSignatureFont,
        loanOfficerSignatureIpAddress: signatureData?.auditLog?.ipAddress || existingPreApproval?.loanOfficerSignatureIpAddress,
        loanOfficerSignatureUserAgent: signatureData?.auditLog?.userAgent || existingPreApproval?.loanOfficerSignatureUserAgent,
        loanOfficerSignatureAuditLog: signatureData?.auditLog || existingPreApproval?.loanOfficerSignatureAuditLog,
        hasLoanOfficerSignature: hasLoanOfficerSignature ||
          existingPreApproval?.hasLoanOfficerSignature ||
          Boolean(signatureData?.dataUrl || existingPreApproval?.loanOfficerSignatureDataUrl)
      };

      const savedId = await SupabasePreApprovalService.savePreApproval(preApprovalData);
      toast.success('Pre-approval saved successfully');
      
      // Fetch the full saved pre-approval data
      const reloadedPreApproval = await SupabasePreApprovalService.getPreApprovalById(savedId);
      if (!reloadedPreApproval) {
        throw new Error('Failed to retrieve saved pre-approval');
      }
      return reloadedPreApproval;
    } catch (error) {
      logger.error('Failed to save pre-approval:', error);
      toast.error('Failed to save pre-approval');
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const handleESign = async () => {
    if (!validateStep2()) {
      toast.error('Please fix the errors before signing');
      return;
    }

    // Save draft first to get pre-approval ID
    const saved = await handleSave();
    if (saved) {
      setSavedPreApproval(saved);
      setShowSignatureDialog(true);
    }
  };

  const handleSignatureComplete = async (data: SignatureData) => {
    if (!savedPreApproval) {
return;
}

    try {
      // Save signature to database
      await SupabasePreApprovalService.updatePreApproval(savedPreApproval.id, {
        loanOfficerSignatureDate: new Date().toISOString(),
        loanOfficerSignatureDataUrl: data.dataUrl,
        loanOfficerSignatureMethod: data.metadata.method,
        loanOfficerSignatureFont: data.metadata.font,
        loanOfficerSignatureIpAddress: data.auditLog?.ipAddress,
        loanOfficerSignatureUserAgent: data.auditLog?.userAgent,
        loanOfficerSignatureAuditLog: data.auditLog,
        hasLoanOfficerSignature: true
      });

      setSignatureData(data);
      setHasLoanOfficerSignature(true);
      setShowSignatureDialog(false);

      // Reload pre-approval to show signature in preview
      const updated = await SupabasePreApprovalService.getPreApprovalById(savedPreApproval.id);
      if (updated) {
        setSavedPreApproval(updated);
        setFormData(prev => ({
          ...prev,
          loanOfficerSignatureDataUrl: updated.loanOfficerSignatureDataUrl,
          loanOfficerSignatureDate: updated.loanOfficerSignatureDate,
          loanOfficerSignatureIpAddress: updated.loanOfficerSignatureIpAddress,
          hasLoanOfficerSignature: updated.hasLoanOfficerSignature
        }));
      }

      toast.success('E-signature captured successfully');
    } catch (error) {
      logger.error('Failed to save signature:', error);
      toast.error('Failed to save signature. Please try again.');
    }
  };

  const getStepProgress = () => {
    return (currentStep / 2) * 100;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            <h2 className="text-2xl font-bold mb-2">Choose a Template</h2>
            <p className="text-muted-foreground mb-6">
              Select a visual template for your pre-approval letter
            </p>
            <VisualTemplateGallery
              onSelectTemplate={handleTemplateSelect}
              onCreateNew={() => {
                toast.info('Please create and save a template first');
                navigate('/tools/pre-approval-templates');
              }}
              autoSelectDefault={!!prefilledData && !hasAutoSelected}
            />
          </div>
        );

      case 2:
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-full">
            {/* Form Section */}
            <div className={cn(
              "lg:col-span-1",
              showPreview ? "lg:col-span-1" : "lg:col-span-2"
            )}>
              <div className="mb-4">
                <h2 className="text-2xl font-bold mb-2">Enter Pre-Approval Details</h2>
                <p className="text-muted-foreground">
                  Fill in the borrower and loan information
                </p>
              </div>

              <div className="space-y-6">
              {/* Borrower Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Borrower Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Salutation field for formal addressing */}
                  <div className="w-1/3">
                    <Label className="text-sm">Title (Optional)</Label>
                    <Select
                      value={formData.borrowerInfo?.salutation || 'none'}
                      onValueChange={(value) => handleFieldChange('borrowerInfo', 'salutation', value === 'none' ? '' : value)}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select title" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="Mr.">Mr.</SelectItem>
                        <SelectItem value="Mrs.">Mrs.</SelectItem>
                        <SelectItem value="Ms.">Ms.</SelectItem>
                        <SelectItem value="Dr.">Dr.</SelectItem>
                        <SelectItem value="Prof.">Prof.</SelectItem>
                        <SelectItem value="Rev.">Rev.</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">First Name*</Label>
                      <Input
                        className={cn("h-9", errors['borrowerInfo.firstName'] ? 'border-red-500' : '')}
                        value={formData.borrowerInfo?.firstName || ''}
                        onChange={(e) => handleFieldChange('borrowerInfo', 'firstName', e.target.value)}
                        maxLength={30}
                      />
                      {errors['borrowerInfo.firstName'] && (
                        <p className="text-sm text-red-500 mt-1">{errors['borrowerInfo.firstName']}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-sm">Last Name*</Label>
                      <Input
                        className={cn("h-9", errors['borrowerInfo.lastName'] ? 'border-red-500' : '')}
                        value={formData.borrowerInfo?.lastName || ''}
                        onChange={(e) => handleFieldChange('borrowerInfo', 'lastName', e.target.value)}
                        maxLength={30}
                      />
                      {errors['borrowerInfo.lastName'] && (
                        <p className="text-sm text-red-500 mt-1">{errors['borrowerInfo.lastName']}</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Email*</Label>
                    <Input
                      className={cn("h-9", errors['borrowerInfo.email'] ? 'border-red-500' : '')}
                      type="email"
                      value={formData.borrowerInfo?.email || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleFieldChange('borrowerInfo', 'email', value);

                        // Real-time validation
                        const error = validateEmail(value);
                        setErrors(prev => {
                          const newErrors = { ...prev };
                          if (error) {
                            newErrors['borrowerInfo.email'] = error;
                          } else {
                            delete newErrors['borrowerInfo.email'];
                          }
                          return newErrors;
                        });
                      }}
                      maxLength={100}
                    />
                    {errors['borrowerInfo.email'] && (
                      <p className="text-sm text-red-500 mt-1">{errors['borrowerInfo.email']}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-sm">Phone*</Label>
                    <Input
                      className={cn("h-9", errors['borrowerInfo.phone'] ? 'border-red-500' : '')}
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formatPhoneNumber(formData.borrowerInfo?.phone || '')}
                      onChange={(e) => {
                        // Store the raw phone digits
                        const cleaned = e.target.value.replace(/\D/g, '');
                        handleFieldChange('borrowerInfo', 'phone', cleaned);

                        // Real-time validation
                        const phoneDigits = cleaned.replace(/\D/g, '');
                        setErrors(prev => {
                          const newErrors = { ...prev };
                          if (!cleaned) {
                            newErrors['borrowerInfo.phone'] = 'Phone is required';
                          } else if (phoneDigits.length !== 10) {
                            newErrors['borrowerInfo.phone'] = 'Please enter a valid 10-digit phone number';
                          } else {
                            delete newErrors['borrowerInfo.phone'];
                          }
                          return newErrors;
                        });
                      }}
                      maxLength={14}
                    />
                    {errors['borrowerInfo.phone'] && (
                      <p className="text-sm text-red-500 mt-1">{errors['borrowerInfo.phone']}</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {!showCoBorrower ? (
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowCoBorrower(true);
                      setFormData(prev => ({
                        ...prev,
                        coBorrowerInfo: prev.coBorrowerInfo ?? {
                          salutation: '',
                          firstName: '',
                          lastName: '',
                          email: '',
                          phone: ''
                        }
                      }));
                    }}
                  >
                    Add Co-Borrower
                  </Button>
                </div>
              ) : (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Co-Borrower Information
                    </CardTitle>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={clearCoBorrower}
                    >
                      Remove Co-Borrower
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="w-1/3">
                      <Label className="text-sm">Title (Optional)</Label>
                      <Select
                        value={formData.coBorrowerInfo?.salutation || 'none'}
                        onValueChange={(value) => handleFieldChange('coBorrowerInfo', 'salutation', value === 'none' ? '' : value)}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select title" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="Mr.">Mr.</SelectItem>
                          <SelectItem value="Mrs.">Mrs.</SelectItem>
                          <SelectItem value="Ms.">Ms.</SelectItem>
                          <SelectItem value="Dr.">Dr.</SelectItem>
                          <SelectItem value="Prof.">Prof.</SelectItem>
                          <SelectItem value="Rev.">Rev.</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm">First Name*</Label>
                        <Input
                          className={cn("h-9", errors['coBorrowerInfo.firstName'] ? 'border-red-500' : '')}
                          value={formData.coBorrowerInfo?.firstName || ''}
                          onChange={(e) => handleFieldChange('coBorrowerInfo', 'firstName', e.target.value)}
                          maxLength={30}
                        />
                        {errors['coBorrowerInfo.firstName'] && (
                          <p className="text-sm text-red-500 mt-1">{errors['coBorrowerInfo.firstName']}</p>
                        )}
                      </div>
                      <div>
                        <Label className="text-sm">Last Name*</Label>
                        <Input
                          className={cn("h-9", errors['coBorrowerInfo.lastName'] ? 'border-red-500' : '')}
                          value={formData.coBorrowerInfo?.lastName || ''}
                          onChange={(e) => handleFieldChange('coBorrowerInfo', 'lastName', e.target.value)}
                          maxLength={30}
                        />
                        {errors['coBorrowerInfo.lastName'] && (
                          <p className="text-sm text-red-500 mt-1">{errors['coBorrowerInfo.lastName']}</p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm">Email (Optional)</Label>
                        <Input
                          className={cn("h-9", errors['coBorrowerInfo.email'] ? 'border-red-500' : '')}
                          type="email"
                          placeholder="coborrower@email.com"
                          value={formData.coBorrowerInfo?.email || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleFieldChange('coBorrowerInfo', 'email', value);
                            // Real-time validation: validate format only if non-empty
                            setErrors(prev => {
                              const newErrors = { ...prev };
                              if (value.trim()) {
                                const error = validateEmail(value);
                                if (error) {
                                  newErrors['coBorrowerInfo.email'] = error;
                                } else {
                                  delete newErrors['coBorrowerInfo.email'];
                                }
                              } else {
                                delete newErrors['coBorrowerInfo.email'];
                              }
                              return newErrors;
                            });
                          }}
                          maxLength={100}
                        />
                        {errors['coBorrowerInfo.email'] && (
                          <p className="text-sm text-red-500 mt-1">{errors['coBorrowerInfo.email']}</p>
                        )}
                      </div>
                      <div>
                        <Label className="text-sm">Mobile Phone (Optional)</Label>
                        <Input
                          className={cn("h-9", errors['coBorrowerInfo.phone'] ? 'border-red-500' : '')}
                          type="tel"
                          placeholder="(555) 123-4567"
                          value={formatPhoneNumber(formData.coBorrowerInfo?.phone || '')}
                          onChange={(e) => {
                            const cleaned = e.target.value.replace(/\D/g, '');
                            handleFieldChange('coBorrowerInfo', 'phone', cleaned);
                            // Real-time validation: validate 10 digits only if non-empty
                            setErrors(prev => {
                              const newErrors = { ...prev };
                              if (cleaned) {
                                if (cleaned.length !== 10) {
                                  newErrors['coBorrowerInfo.phone'] = 'Please enter a valid 10-digit phone number';
                                } else {
                                  delete newErrors['coBorrowerInfo.phone'];
                                }
                              } else {
                                delete newErrors['coBorrowerInfo.phone'];
                              }
                              return newErrors;
                            });
                          }}
                          maxLength={14}
                        />
                        {errors['coBorrowerInfo.phone'] && (
                          <p className="text-sm text-red-500 mt-1">{errors['coBorrowerInfo.phone']}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Loan Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Loan Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Pre-approved LOAN amount*</Label>
                      <Input
                        className={cn("h-9", errors['loanDetails.loanAmount'] ? 'border-red-500' : '')}
                        type="text"
                        inputMode="numeric"
                        placeholder="500,000"
                        value={formatCurrency(formData.loanDetails?.loanAmount?.toString() || '')}
                        onChange={(e) => {
                          const cleaned = e.target.value.replace(/[^0-9]/g, '');
                          const value = parseFloat(cleaned) || 0;
                          handleFieldChange('loanDetails', 'loanAmount', value);

                          // Real-time validation
                          setErrors(prev => {
                            const newErrors = { ...prev };
                            if (value <= 0) {
                              newErrors['loanDetails.loanAmount'] = 'Loan amount must be greater than $0';
                            } else {
                              delete newErrors['loanDetails.loanAmount'];
                            }

                            // Re-validate purchase price if it exists
                            const purchasePrice = formData.loanDetails?.purchasePrice || 0;
                            if (purchasePrice > 0 && purchasePrice < value) {
                              newErrors['loanDetails.purchasePrice'] = 'Purchase price must be at least the loan amount';
                            } else if (purchasePrice > 0) {
                              delete newErrors['loanDetails.purchasePrice'];
                            }

                            return newErrors;
                          });
                        }}
                      />
                      {errors['loanDetails.loanAmount'] && (
                        <p className="text-sm text-red-500 mt-1">{errors['loanDetails.loanAmount']}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-sm">Purchase Price*</Label>
                      <Input
                        className={cn("h-9", errors['loanDetails.purchasePrice'] ? 'border-red-500' : '')}
                        type="text"
                        inputMode="numeric"
                        placeholder="600,000"
                        value={formatCurrency(formData.loanDetails?.purchasePrice?.toString() || '')}
                        onChange={(e) => {
                          const cleaned = e.target.value.replace(/[^0-9]/g, '');
                          const value = parseFloat(cleaned) || 0;
                          handleFieldChange('loanDetails', 'purchasePrice', value);

                          // Real-time validation
                          setErrors(prev => {
                            const newErrors = { ...prev };
                            const loanAmount = formData.loanDetails?.loanAmount || 0;

                            if (value <= 0) {
                              newErrors['loanDetails.purchasePrice'] = 'Purchase price must be greater than $0';
                            } else if (value < loanAmount) {
                              newErrors['loanDetails.purchasePrice'] = 'Purchase price must be at least the loan amount';
                            } else {
                              delete newErrors['loanDetails.purchasePrice'];
                            }

                            return newErrors;
                          });
                        }}
                      />
                      {errors['loanDetails.purchasePrice'] && (
                        <p className="text-sm text-red-500 mt-1">{errors['loanDetails.purchasePrice']}</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Loan Type</Label>
                    <Select
                      value={formData.loanDetails?.loanType || 'conventional'}
                      onValueChange={(value) => handleFieldChange('loanDetails', 'loanType', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="conventional">Conventional</SelectItem>
                        <SelectItem value="fha">FHA</SelectItem>
                        <SelectItem value="va">VA</SelectItem>
                        <SelectItem value="usda">USDA</SelectItem>
                        <SelectItem value="jumbo">Jumbo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Auto-calculated fields */}
                  <div className="grid grid-cols-3 gap-3 p-3 bg-gray-50 rounded-lg text-sm">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">Down Payment</p>
                      <p className="font-semibold">${calculations.downPaymentAmount.toLocaleString()}</p>
                      <p className="text-sm text-muted-foreground">({calculations.downPaymentPercent.toFixed(1)}%)</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">LTV Ratio</p>
                      <p className="font-semibold">{formatPercentage(calculations.ltv, 1)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">PMI Required</p>
                      <p className="font-semibold">{calculations.requiresPMI ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Property Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Home className="w-5 h-5" />
                    Property Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Property Type</Label>
                      <Select
                        value={formData.propertyInfo?.propertyType || 'single-family'}
                        onValueChange={(value) => handleFieldChange('propertyInfo', 'propertyType', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single-family">Single Family</SelectItem>
                          <SelectItem value="condo">Condo</SelectItem>
                          <SelectItem value="townhouse">Townhouse</SelectItem>
                          <SelectItem value="multi-family">Multi-Family</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Occupancy</Label>
                      <Select
                        value={formData.propertyInfo?.occupancy || 'primary'}
                        onValueChange={(value) => handleFieldChange('propertyInfo', 'occupancy', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="primary">Primary Residence</SelectItem>
                          <SelectItem value="secondary">Secondary Home</SelectItem>
                          <SelectItem value="investment">Investment Property</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Property Address</Label>
                    <Select
                      value={formData.propertyInfo?.address === 'TBD' ? 'tbd' : 'actual'}
                      onValueChange={(value) => {
                        if (value === 'tbd') {
                          handleFieldChange('propertyInfo', 'address', 'TBD');
                          handleFieldChange('propertyInfo', 'city', '');
                          handleFieldChange('propertyInfo', 'state', '');
                          handleFieldChange('propertyInfo', 'zipCode', '');
                        } else {
                          handleFieldChange('propertyInfo', 'address', '');
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tbd">To Be Determined (TBD)</SelectItem>
                        <SelectItem value="actual">Enter Address</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.propertyInfo?.address !== 'TBD' && (
                    <>
                      <div>
                        <Label className="text-sm">Street Address*</Label>
                        <Input
                          className={cn("h-9", errors['propertyInfo.address'] ? 'border-red-500' : '')}
                          value={formData.propertyInfo?.address || ''}
                          onChange={(e) => handleFieldChange('propertyInfo', 'address', e.target.value)}
                          maxLength={100}
                        />
                        {errors['propertyInfo.address'] && (
                          <p className="text-sm text-red-500 mt-1">{errors['propertyInfo.address']}</p>
                        )}
                      </div>

                      {/* Alert when no licensed states are configured */}
                      {!profileLoading && filteredStates.length === 0 && (
                        <Alert className="mb-4">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>
                            Please configure your licensed states in your profile before creating pre-approvals.
                            <Button
                              variant="link"
                              className="p-0 h-auto ml-1"
                              onClick={() => navigate('/settings/profile')}
                            >
                              Go to Profile Settings
                            </Button>
                          </AlertDescription>
                        </Alert>
                      )}

                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-sm">City*</Label>
                          <Input
                            className={cn("h-9", errors['propertyInfo.city'] ? 'border-red-500' : '')}
                            value={formData.propertyInfo?.city || ''}
                            onChange={(e) => handleFieldChange('propertyInfo', 'city', e.target.value)}
                            maxLength={50}
                          />
                          {errors['propertyInfo.city'] && (
                            <p className="text-sm text-red-500 mt-1">{errors['propertyInfo.city']}</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-sm">State*</Label>
                          <Select
                            value={formData.propertyInfo?.state || ''}
                            onValueChange={(value) => handleFieldChange('propertyInfo', 'state', value)}
                            disabled={profileLoading || filteredStates.length === 0}
                          >
                            <SelectTrigger className={cn("h-9", errors['propertyInfo.state'] ? 'border-red-500' : '')}>
                              <SelectValue placeholder={
                                profileLoading
                                  ? "Loading states..."
                                  : filteredStates.length === 0
                                    ? "No states configured"
                                    : "Select state"
                              } />
                            </SelectTrigger>
                            <SelectContent>
                              {filteredStates.map((state) => (
                                <SelectItem key={state.value} value={state.value}>
                                  {state.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {errors['propertyInfo.state'] && (
                            <p className="text-sm text-red-500 mt-1">{errors['propertyInfo.state']}</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-sm">ZIP Code*</Label>
                          <Input
                            className={cn("h-9", errors['propertyInfo.zipCode'] ? 'border-red-500' : '')}
                            type="text"
                            placeholder="12345"
                            value={formData.propertyInfo?.zipCode || ''}
                            onChange={(e) => {
                              // Only allow digits, max 5 characters
                              const cleaned = e.target.value.replace(/\D/g, '').slice(0, 5);
                              handleFieldChange('propertyInfo', 'zipCode', cleaned);
                            }}
                            maxLength={5}
                          />
                          {errors['propertyInfo.zipCode'] && (
                            <p className="text-sm text-red-500 mt-1">{errors['propertyInfo.zipCode']}</p>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Expiration Date */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Expiration Date
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div>
                    <Label className="text-sm">Pre-Approval Valid Until</Label>
                    <Input
                      className="h-9"
                      type="date"
                      value={formData.expirationDate || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, expirationDate: e.target.value }))}
                      min={new Date().toISOString().split('T')[0]}
                      max={getExpirationDate(PRE_APPROVAL_CONSTANTS.EXPIRATION.MAX_DAYS)}
                    />
                  </div>
                </CardContent>
              </Card>
              </div>
            </div>

            {/* Preview Section */}
            {showPreview && selectedTemplate && (
              <div className="lg:col-span-1">
                <div className="sticky top-4 h-full">
                  <VisualTemplatePreview
                    template={selectedTemplate}
                    formData={{
                      // LO/company profile fields (loaded async, only spread when available)
                      ...(templateVars ? {
                        loanOfficerName: templateVars.loanOfficerName,
                        loanOfficerFirstName: templateVars.loanOfficerFirstName,
                        loanOfficerLastName: templateVars.loanOfficerLastName,
                        loanOfficerTitle: templateVars.loanOfficerTitle,
                        loanOfficerNMLS: templateVars.loanOfficerNMLS,
                        loanOfficerPhone: templateVars.loanOfficerPhone,
                        loanOfficerMobile: templateVars.loanOfficerMobile,
                        loanOfficerEmail: templateVars.loanOfficerEmail,
                        companyName: templateVars.companyName,
                        companyNMLS: templateVars.companyNMLS,
                        companyEmail: templateVars.companyEmail,
                        companyWebsite: templateVars.companyWebsite,
                        companyPhone: templateVars.companyPhone,
                        // Flatten companyAddress object into string + individual fields
                        companyAddress: templateVars.companyAddress
                          ? `${templateVars.companyAddress.street || ''}, ${templateVars.companyAddress.city || ''}, ${templateVars.companyAddress.state || ''} ${templateVars.companyAddress.zip || ''}`.replace(/,\s*,/g, ',').trim()
                          : undefined,
                        companyStreet: templateVars.companyAddress?.street,
                        companyCity: templateVars.companyAddress?.city,
                        companyState: templateVars.companyAddress?.state,
                        companyZip: templateVars.companyAddress?.zip
                      } : {}),
                      // User-entered fields (always win over templateVars)
                      borrowerSalutation: formData.borrowerInfo?.salutation,
                      borrowerFirstName: formData.borrowerInfo?.firstName,
                      borrowerLastName: formData.borrowerInfo?.lastName,
                      borrowerEmail: formData.borrowerInfo?.email,
                      borrowerPhone: formData.borrowerInfo?.phone,
                      coBorrowerSalutation: formData.coBorrowerInfo?.salutation,
                      coBorrowerFirstName: formData.coBorrowerInfo?.firstName,
                      coBorrowerLastName: formData.coBorrowerInfo?.lastName,
                      coBorrowerEmail: formData.coBorrowerInfo?.email,
                      coBorrowerPhone: formData.coBorrowerInfo?.phone,
                      propertyAddress: formData.propertyInfo?.address,
                      propertyCity: formData.propertyInfo?.city,
                      propertyState: formData.propertyInfo?.state,
                      propertyZip: formData.propertyInfo?.zipCode,
                      propertyType: formData.propertyInfo?.propertyType,
                      propertyOccupancy: formData.propertyInfo?.occupancy,
                      loanAmount: formData.loanDetails?.loanAmount?.toString(),
                      purchasePrice: formData.loanDetails?.purchasePrice?.toString(),
                      loanType: formData.loanDetails?.loanType,
                      downPayment: calculations.downPaymentAmount.toString(),
                      downPaymentPercent: calculations.downPaymentPercent.toString(),
                      expirationDate: formData.expirationDate
                    }}
                    loanOfficerSignatureDataUrl={savedPreApproval?.loanOfficerSignatureDataUrl || prefilledData?.loanOfficerSignatureDataUrl}
                    hasLoanOfficerSignature={hasLoanOfficerSignature}
                    loanOfficerSignatureDate={savedPreApproval?.loanOfficerSignatureDate || prefilledData?.loanOfficerSignatureDate}
                    loanOfficerSignatureIpAddress={savedPreApproval?.loanOfficerSignatureIpAddress || prefilledData?.loanOfficerSignatureIpAddress}
                  />
                </div>
              </div>
            )}
          </div>
        );

    }
  };

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="border-b bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {currentStep === 2 && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group"
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                <span>Back</span>
              </button>
            )}
            {currentStep === 1 && (
              <>
                <h1 className="text-2xl font-bold">Create Pre-Approval</h1>
                <Progress value={getStepProgress()} className="w-32" />
                <span className="text-sm text-muted-foreground">
                  Step {currentStep} of 2
                </span>
              </>
            )}
            {currentStep === 2 && (
              <div className="flex items-center gap-4">
                <h1 className="text-2xl font-bold">Create Pre-Approval</h1>
                <Progress value={getStepProgress()} className="w-32" />
                <span className="text-sm text-muted-foreground">
                  Step {currentStep} of 2
                </span>
              </div>
            )}
          </div>
          {currentStep === 2 && (
            <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPreview(!showPreview)}
            >
              {showPreview ? (
                <>
                  <EyeOff className="w-4 h-4 mr-2" />
                  Hide Preview
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Show Preview
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                const saved = await handleSave();
                if (saved && onComplete) {
                  onComplete(saved.id);
                } else if (saved) {
                  navigate('/tools/pre-approval');
                }
              }}
              disabled={isSaving || !selectedTemplate}
            >
              <Save className="w-4 h-4 mr-2" />
              Save Draft
            </Button>
            <Button
              variant={hasLoanOfficerSignature ? "outline" : "default"}
              size="sm"
              onClick={handleESign}
              disabled={!selectedTemplate || isSaving || hasLoanOfficerSignature}
              className={hasLoanOfficerSignature ? 'border-green-600 text-green-700' : ''}
              title={hasLoanOfficerSignature ? 'Pre-approval already signed' : 'Sign the pre-approval'}
            >
              <Pen className="w-4 h-4 mr-2" />
              {hasLoanOfficerSignature ? 'Signed' : 'E-Sign'}
            </Button>
            <Button
              size="sm"
              onClick={async () => {
                if (!validateStep2()) {
                  toast.error('Please fix the errors before sending');
                  return;
                }

                // Check if template requires signature
                const templateRequiresSignature = selectedTemplate?.elements?.some(
                  element => element.type === 'signature' ||
                             (element.content && element.content.includes('{{loanOfficerSignature}}'))
                );

                // Validate signature before opening send modal
                if (templateRequiresSignature && !hasLoanOfficerSignature) {
                  toast.error('Please e-sign this pre-approval before sending', {
                    description: 'This template requires your signature'
                  });
                  return;
                }

                const saved = await handleSave();
                if (saved) {
                  setSavedPreApproval(saved);
                  setShowSendModal(true);
                }
              }}
              disabled={!selectedTemplate || isSaving}
              className="bg-[#183C2E] hover:bg-[#153628]"
              title={!selectedTemplate ? 'Select a template first' : ''}
            >
              <Send className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : 'Send'}
            </Button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
        {renderStepContent()}
      </div>

      {/* Footer */}
      <div className="border-t bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          {currentStep === 1 && (
            <Button
              variant="outline"
              onClick={handleBack}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          )}
          {currentStep === 2 && <div />}

          <div className="flex gap-3">
            {currentStep === 1 && (
              <Button onClick={handleNext}>
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* Send Modal */}
      {showSendModal && savedPreApproval && (
        <PreApprovalSendModal
          isOpen={showSendModal}
          onClose={() => {
            setShowSendModal(false);
            setSavedPreApproval(null);
          }}
          preApproval={savedPreApproval}
          onSend={async () => {
            toast.success('Pre-approval sent successfully');
            if (onComplete) {
              onComplete(savedPreApproval.id);
            } else {
              navigate('/tools/pre-approval');
            }
          }}
        />
      )}

      {/* E-Signature Dialog */}
      <SignatureDialog
        open={showSignatureDialog}
        onOpenChange={setShowSignatureDialog}
        title="Pre-Approval E-Signature"
        description={`Sign as ${profile?.name || 'Loan Officer'}`}
        requireConsent={true}
        captureIpAddress={true}
        ipAddressEndpoint={`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-client-ip`}
        onSignatureComplete={handleSignatureComplete}
      />
    </div>
  );
};
